import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    FormControl,
    FormLabel,
    Typography,
    FormGroup,
    RadioGroup,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import OptionRow from './OptionRow';

export default function DishVariantsSelector({
    dish,
    variantsSelection,
    setVariantsSelection,
    variantErrors = {},
    //setVariantErrors = () => { },
    variantRefs = { current: {} },
    onDeletedNotesChange, // Prop per comunicare note "NO"
    mode = "client",
}) {
    const isOwner = mode === "owner";
    const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'));

    const lastDeletedIds = useRef([]);

    // 🔹 deletedOptions inizializzato dal dish.variants.deletedOptions
    const [deletedOptions, setDeletedOptions] = useState(() => {
        if (!isOwner || !dish?.variants) return new Set();

        const initialDeleted = new Set();
        dish.variants.forEach(v => v.deletedOptions?.forEach(optId => initialDeleted.add(optId)));
        return initialDeleted;
    });

    // Aggiorna le note "NO ..." ogni volta che deletedOptions cambia
    useEffect(() => {
        const deletedIds = Array.from(deletedOptions);

        // 💡 PASSO CRUCIALE PER LA STABILITÀ: Controlla se l'array è REALMENTE cambiato

        // Confrontiamo la lunghezza per un controllo veloce
        const isLengthEqual = deletedIds.length === lastDeletedIds.current.length;

        // Se le lunghezze sono uguali, verifichiamo che gli elementi siano gli stessi 
        // (l'ordine non è importante, quindi usiamo includes)
        const isContentEqual = isLengthEqual &&
            deletedIds.every(id => lastDeletedIds.current.includes(id)) &&
            lastDeletedIds.current.every(id => deletedIds.includes(id));

        if (isContentEqual) {
            // Se non c'è stata una modifica sostanziale nell'elenco degli ID, interrompi.
            return;
        }

        // Aggiorna il riferimento per il prossimo confronto
        lastDeletedIds.current = deletedIds;

        // -----------------------------------------------------
        // Logica di calcolo delle notes (come prima)
        // -----------------------------------------------------
        const notes = deletedIds
            .map(optId => {
                for (const variant of dish.variants) {
                    const opt = variant.options.find(o => o.id === optId);
                    if (opt) return `NO ${opt.label}`;
                }
                return null;
            })
            .filter(Boolean)
            .join(', ');

        // Chiama il genitore con la stringa e l'array di ID
        onDeletedNotesChange?.(notes, deletedIds); // Passa sia la stringa che l'array di ID

        // Rimuoviamo dish.variants dalle dipendenze per ridurre i re-trigger inutili, 
        // mantenendo solo lo stato interno e la prop di callback.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deletedOptions, onDeletedNotesChange]);

    const getOption = (variantId, optionId) => dish.variants.find(v => v.id === variantId)?.options.find(o => o.id === optionId);

    const removeSelected = (variantId, optionId) => {
        const updated = variantsSelection.map(v => {
            if (v.id === variantId) {
                return { ...v, selected: v.selected.filter(s => s.id !== optionId) };
            }
            return v;
        });
        return updated.filter(v => v.selected.length > 0);
    };

    const handleChange = (e, variant) => {
        const variantId = variant.id;
        const optionId = e.target.value;
        const isChecked = e.target.checked;
        const option = getOption(variantId, optionId);
        if (!option) return;

        if (isOwner && deletedOptions.has(optionId)) {
            setDeletedOptions(prev => {
                const next = new Set(prev);
                next.delete(optionId);
                return next;
            });
        }

        const isMulti = variant.multi === 1;
        let updated = [...variantsSelection];

        if (isMulti) {
            const index = updated.findIndex(v => v.id === variantId);
            if (isChecked) {
                if (index === -1) updated.push({ id: variantId, name: variant.name, selected: [option] });
                else updated[index] = { ...updated[index], selected: [...updated[index].selected, option] };
            } else updated = removeSelected(variantId, optionId);

            setVariantsSelection(updated);
            return;
        }

        // SINGLE
        updated = updated.filter(v => v.id !== variantId);
        updated.push({ id: variantId, name: variant.name, selected: [option] });
        setVariantsSelection(updated);
    };

    const handleToggleDelete = (variantId, optionId) => {
        // Aggiorna deletedOptions nello stato locale
        setDeletedOptions(prev => {
            const next = new Set(prev);
            if (next.has(optionId)) next.delete(optionId);
            else next.add(optionId);
            return next;
        });

        // Aggiorna anche selected per rimuovere l'opzione selezionata
        const updatedSelection = variantsSelection
            .map(v => ({ ...v, selected: v.selected?.filter(o => o.id !== optionId) }))
            .filter(v => v.selected && v.selected.length > 0);
        setVariantsSelection(updatedSelection);
    };

    const sortOptions = options => {
        const noThanks = options.filter(o => o.label.toLowerCase().includes('no, grazie'));
        const others = options.filter(o => !o.label.toLowerCase().includes('no, grazie')).sort((a, b) => a.label.localeCompare(b.label));
        return [...noThanks, ...others];
    };

    const getSingleSelectedId = (variantId) => variantsSelection.find(v => v.id === variantId)?.selected[0]?.id || '';

    return (
        <Box sx={{ width: '100%', padding: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2 }}>
                {dish.variants.map((variant) => {
                    const selected = variantsSelection.find(v => v.id === variant.id);
                    const hasError = variantErrors[variant.id];
                    const isMulti = variant.multi === 1;

                    return (
                        <Box
                            key={variant.id}
                            ref={el => (variantRefs.current[variant.id] = el)}
                            sx={{
                                width: isMobile ? '100%' : 'calc(25% - 16px)',
                                minWidth: 300,
                                maxWidth: 450,
                                backgroundColor: 'rgba(29,29,29,0.8)',
                                padding: 2,
                                borderRadius: 2,
                                border: hasError ? '2px solid' : 'none',
                                borderColor: 'error.main',
                                boxShadow: hasError ? `0 0 12px 3px rgba(255,0,0,0.5)` : '5px 5px 10px rgba(0,0,0,0.5)',
                            }}
                        >
                            <FormControl component="fieldset" fullWidth>
                                <FormLabel>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase', color: 'primary.light', borderBottom: '2px solid', mb: 1.5 }}>
                                        {variant.name}
                                    </Typography>
                                </FormLabel>

                                {isMulti ? (
                                    <FormGroup>
                                        {sortOptions(variant.options).map((opt, idx) => (
                                            <OptionRow
                                                key={opt.id}
                                                option={opt}
                                                variant={variant}
                                                isChecked={selected?.selected.some(s => s.id === opt.id)}
                                                handleChange={handleChange}
                                                isMulti
                                                isLast={idx === variant.options.length - 1}
                                                mode={mode}
                                                isDeleted={isOwner && deletedOptions.has(opt.id)}
                                                onToggleDelete={handleToggleDelete}
                                            />
                                        ))}
                                    </FormGroup>
                                ) : (
                                    <RadioGroup value={getSingleSelectedId(variant.id)} onChange={e => handleChange(e, variant)}>
                                        {hasError && <Typography variant="body2" color="error" mb={1}>(scelta obbligatoria)</Typography>}
                                        {sortOptions(variant.options).map((opt, idx) => (
                                            <OptionRow
                                                key={opt.id}
                                                option={opt}
                                                variant={variant}
                                                isChecked={getSingleSelectedId(variant.id) === opt.id}
                                                isMulti={false}
                                                isLast={idx === variant.options.length - 1}
                                                mode={mode}
                                                isDeleted={isOwner && deletedOptions.has(opt.id)}
                                                onToggleDelete={handleToggleDelete}
                                            />
                                        ))}
                                    </RadioGroup>
                                )}
                            </FormControl>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}
